import { integrationApi } from "@repo/api";
import { InputField, Typography } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { theme } from "../../../constants/theme";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { useTestConnection } from "../../../hooks/useTestConnection";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { FormSection } from "../../FormSection";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import { TestConnectionBlock } from "../../TestConnectionBlock";
import { AdditionalConfigurationSection } from "../../aws/AWSIntegrationForm/AdditionalConfigurationSection";
import { getDefaultTime } from "../../aws/AWSIntegrationForm/utils";

export interface ServiceNowDiscoveryFormData {
  name: string;
  description: string;
  domain: string;
  apiKey: string;
  isValid: boolean;
  cftDownloaded: boolean;
}

interface ServiceNowDiscoveryFormProps {
  integrationType: string;
  integrationId?: number;
  onFormDataChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export function ServiceNowDiscoveryForm({
  integrationType,
  integrationId,
  onFormDataChange,
}: ServiceNowDiscoveryFormProps) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [importTime, setImportTime] = useState<Date | null>(() =>
    getDefaultTime(),
  );
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const hasPrefilledIntegrationRef = useRef(false);
  const hasPrefilledCredentialsRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;

  const { data: integrationData } = useIntegrationById(integrationId);
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: featuresResponse } =
    useIntegrationTypeFeatures(integrationType);
  const featureNames = useMemo(
    () =>
      featuresResponse?.features
        ? featuresResponse.features.map((f) => f.featureName)
        : [],
    [featuresResponse?.features],
  );

  useEffect(() => {
    if (!integrationId || !integrationData) return;

    if (!hasPrefilledIntegrationRef.current) {
      setName(integrationData.displayName ?? "");
      setDescription((integrationData.metadata?.description as string) ?? "");
      setDomain(
        (integrationData.metadata?.accountId as string) ??
          ((credentials?.metadata as Record<string, unknown> | undefined)
            ?.baseUrl as string) ??
          "",
      );

      if (
        integrationData.features?.length &&
        integrationData.features[0].schedules?.length
      ) {
        const cronExpression =
          integrationData.features[0].schedules[0].cronExpression;
        if (cronExpression) {
          const parsed = parseCronToSchedule(cronExpression);
          setFrequency(parsed.frequency);
          if (parsed.time) {
            setImportTime(parsed.time);
          }
        }
      }
      hasPrefilledIntegrationRef.current = true;
    }

    const credsData = credentials?.data as { keyValue?: string } | undefined;
    const prefilledApiKey = credsData?.keyValue;
    if (
      prefilledApiKey != null &&
      prefilledApiKey !== "" &&
      !hasPrefilledCredentialsRef.current
    ) {
      setApiKey(prefilledApiKey);
      const credsMetadata = credentials?.metadata as
        | { baseUrl?: string }
        | undefined;
      if (credsMetadata?.baseUrl) setDomain(credsMetadata.baseUrl);
      hasPrefilledCredentialsRef.current = true;
    }
  }, [
    integrationId,
    integrationData,
    credentials?.data,
    credentials?.metadata,
  ]);

  const testConnection = useTestConnection(() => {
    const trimmedDomain = domain.trim();
    const trimmedApiKey = apiKey.trim();
    if (!trimmedDomain || !trimmedApiKey) {
      return null;
    }
    return {
      integrationType: "NowAssist",
      credentials: {
        type: "API_KEY",
        data: {
          keyName: "x-sn-apikey",
          keyValue: trimmedApiKey,
        },
        metadata: {
          baseUrl: trimmedDomain,
        },
      },
      integrationParameters: {},
    };
  });

  const isNameValid = name.trim().length > 0;
  const isDomainValid = domain.trim().length > 0;
  const isApiKeyValid =
    apiKey.trim().length > 0 || (!!integrationId && !!domain);
  const isValid =
    isNameValid &&
    isDomainValid &&
    isApiKeyValid &&
    (frequency !== "24hours" || importTime !== null);
  const cftDownloaded = isDomainValid && isApiKeyValid;

  useEffect(() => {
    onFormDataChangeRef.current({
      name: name.trim(),
      description: description.trim(),
      domain: domain.trim(),
      apiKey: apiKey,
      frequency,
      importTime,
      isValid,
      cftDownloaded,
      featureNames,
      integrationType,
    });
  }, [
    name,
    description,
    domain,
    apiKey,
    frequency,
    importTime,
    isValid,
    cftDownloaded,
    featureNames,
    integrationType,
  ]);

  return (
    <div
      className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4"
      data-testid="servicenow-discovery-form"
    >
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType={integrationType} />
          <FormSection
            icon="Connect"
            title={intl.formatMessage({
              id: "6c0sHj",
              defaultMessage:
                "What are the connection details of your integration?",
              description: "Connection details section title",
            })}
            subtitle={intl.formatMessage({
              id: "KlVUsw",
              defaultMessage:
                "Some fields have been pre-filled for you, you can update them whenever you like.",
              description: "Connection details section subtitle",
            })}
          >
            <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
              <InputField
                name="name"
                label={
                  <Typography kind="label" htmlFor="servicenow-name">
                    <FormattedMessage
                      id="JeJjeJ"
                      defaultMessage="Integration Name"
                      description="Integration name field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={name}
                setValue={setName}
                isRequired
                isDisabled={!!integrationId}
              />

              <div>
                <Typography
                  kind="label"
                  className="block mb-1"
                  htmlFor="servicenow-description"
                >
                  <FormattedMessage
                    id="TjUCGI"
                    defaultMessage="Integration Description"
                    description="Integration description field label"
                  />
                </Typography>
                <InputField
                  name="description"
                  placeholder=""
                  value={description}
                  setValue={setDescription}
                  kind="multiline"
                  rows={4}
                />
              </div>

              <InputField
                name="domain"
                label={
                  <Typography kind="label" htmlFor="servicenow-domain">
                    <FormattedMessage
                      id="PNbl++"
                      defaultMessage="Domain"
                      description="Domain field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={domain}
                setValue={setDomain}
                isRequired
              />

              <InputField
                name="apiKey"
                label={
                  <Typography kind="label" htmlFor="servicenow-api-key">
                    <FormattedMessage
                      id="QCRYMS"
                      defaultMessage="API Key"
                      description="API Key field label"
                    />
                  </Typography>
                }
                placeholder=""
                type={apiKeyVisible ? "text" : "password"}
                value={apiKey}
                setValue={setApiKey}
                isRequired={!integrationId}
                suffixButtonType="password"
                suffixIsToggle={apiKeyVisible}
                suffixOnClick={() => setApiKeyVisible(!apiKeyVisible)}
              />

              <TestConnectionBlock
                isTestingConnection={testConnection.isTestingConnection}
                connectionStatus={testConnection.connectionStatus}
                onTestConnection={testConnection.handleTestConnection}
                isDisabled={!isDomainValid || !apiKey.trim()}
              />
            </div>
          </FormSection>

          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}
